import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import "@/styles/help.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import RequireAuth from "@/auth/RequireAuth";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import LibraryErrorBoundary from "@/components/LibraryErrorBoundary";
import RouteProgress from "@/components/RouteProgress";
import LoginPage from "@/pages/LoginPage";
import RunsListPage from "@/pages/RunsListPage";
import RunDetailPage from "@/pages/RunDetailPage";
import ReviewerInboxPage from "@/pages/ReviewerInboxPage";

// Build-info constant — forces unique bundle content per deploy so
// the Emergent build cache cannot dedupe a fresh artefact against
// a prior identical-input build. Embedded into the rendered DOM
// as a hidden ``data-build`` attribute on the app shell.
import { BUILD_VERSION } from "@/buildVersion";

// Phase 16 — public marketing site
import PublicLayout from "@/pages/marketing/PublicLayout";
import HomePage from "@/pages/marketing/HomePage";

// Battlespace editor — lazy-loaded so the Cesium footprint never
// enters the main bundle.
const BattlespaceEditorPage = lazy(() =>
    import("@/pages/BattlespaceEditorPage"),
);
import FeaturesPage from "@/pages/marketing/FeaturesPage";
import PublicDemoPage from "@/pages/marketing/PublicDemoPage";
import {
    PublicDemosListPage,
    PublicDemoViewerPage,
} from "@/pages/marketing/PublicDemosPage";
import SecurityPage from "@/pages/marketing/SecurityPage";
import DocsPage from "@/pages/marketing/DocsPage";
import ContactPage from "@/pages/marketing/ContactPage";
import TermsPage from "@/pages/marketing/TermsPage";
import PrivacyPage from "@/pages/marketing/PrivacyPage";
import AupPage from "@/pages/marketing/AupPage";
import NotFoundPage from "@/pages/marketing/NotFoundPage";
// Phase 10 — new public marketing pages added for SEO & content strategy.
import AboutPage from "@/pages/marketing/AboutPage";
import UseCasesPage from "@/pages/marketing/UseCasesPage";
import EvidencePage from "@/pages/marketing/EvidencePage";
import FaqPage from "@/pages/marketing/FaqPage";

// Auth + commercial flows
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import SubscriptionRequiredPage from "@/pages/SubscriptionRequiredPage";

// Operator pages
import ParameterSetsPage from "@/pages/ParameterSetsPage";
import ParameterSetDetailPage from "@/pages/ParameterSetDetailPage";
import ScenariosLibraryPage from "@/pages/ScenariosLibraryPage";
import QuickSimPage from "@/pages/QuickSimPage";
import MatrixRunPage from "@/pages/MatrixRunPage";
import MatrixRunResultsPage from "@/pages/MatrixRunResultsPage";
import HelpPage from "@/pages/HelpPage";
import CampaignsUserGuidePage from "@/pages/CampaignsUserGuidePage";
import AdminBrochureLeadsPage from "@/pages/admin/AdminBrochureLeadsPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import UsersAdminPage from "@/pages/UsersAdminPage";

// Admin
import AdminTenantsPage from "@/pages/admin/AdminTenantsPage";
import AdminTrainingDataPage from "@/pages/admin/AdminTrainingDataPage";
import AdminHomePage from "@/pages/admin/AdminHomePage";
import AdminEngineeringPage from "@/pages/admin/AdminEngineeringPage";
import AdminRawPresetsPage from "@/pages/admin/AdminRawPresetsPage";

// Phase 9 — new pages wiring previously-orphaned endpoints into the IA.
import JobsPage from "@/pages/JobsPage";
import CompareRunsPage from "@/pages/CompareRunsPage";
import GoldenRunsPage from "@/pages/GoldenRunsPage";
import ModelsRegistryPage from "@/pages/ModelsRegistryPage";
import CalibrationSessionsPage from "@/pages/CalibrationSessionsPage";
import LogsPage from "@/pages/LogsPage";

// Module 1 — Simulation Workbench (v2, Phase 12 follow-on).
import WorkbenchPage from "@/pages/WorkbenchPage";

// Module 2 — Airframe library.
import AirframesPage from "@/pages/AirframesPage";

// Pass G — physics-airframe (FDM registry) read-only catalogue.
import PhysicsAirframesCataloguePage from "@/pages/plan/PhysicsAirframesCataloguePage";

// Module 3 — Environment library.
import EnvironmentsPage from "@/pages/EnvironmentsPage";

// Module 4 — Terrain + Obstacles libraries.
import TerrainPage from "@/pages/TerrainPage";
import ObstaclesPage from "@/pages/ObstaclesPage";

// Module 5 — Threats library.
import ThreatsPage from "@/pages/ThreatsPage";

// Module 6 — Guidance profiles library.
import GuidanceProfilesPage from "@/pages/GuidanceProfilesPage";

// Module 7 — Engagements library.
import EngagementsPage from "@/pages/EngagementsPage";

// Phase 3 — ROE / Reverse Optimisation Engine pages.
import OptimisationListPage from "@/pages/OptimisationListPage";
import OptimisationNewPage from "@/pages/OptimisationNewPage";
import OptimisationStudyDetailPage from "@/pages/OptimisationStudyDetailPage";

// Phase 10 — Pk Effectiveness Engine v1 (Baseline campaigns).
// Phase 14.6 (mini-phase) retired the legacy *list* surface; the
// canonical detail page is preserved as the unified
// /campaigns/:id dispatcher's destination.
import PkCampaignDetailPage from "@/pages/PkCampaignDetailPage";

// Phase 13 — Geometry Randomisation Engine (Geometry sweep campaigns).
// Phase 14.6 retired the legacy list surface (same as above); the
// canonical detail page is preserved.
import GeometryCampaignDetailPage from "@/pages/GeometryCampaignDetailPage";
import OptimisationCampaignDetailPage from "@/pages/OptimisationCampaignDetailPage";
import UnifiedCampaignBuilderPage from "@/pages/UnifiedCampaignBuilderPage";
import UnifiedCampaignDetailPage from "@/pages/UnifiedCampaignDetailPage";

// Phase 14.5 — Unified Campaign Builder UX (single /campaigns workflow).
// Phase 14.6 (mini-phase) RESCINDED Phase 14.5 Gate B "Legacy routes
// still live" — bare /pk-campaigns and /geometry-campaigns are no
// longer mounted; bookmarks fall through to the existing catch-all
// `NotFoundPage` at the bottom of this file. Canonical `:campaignId`
// detail routes remain mounted because the unified /campaigns/:id
// dispatcher navigates to them.
import CampaignsListPage from "@/pages/CampaignsListPage";
import NewCampaignWizardPage from "@/pages/NewCampaignWizardPage";
import CampaignDetailDispatchPage from "@/pages/CampaignDetailDispatchPage";
import DegradationProfilesListPage from "@/pages/DegradationProfilesListPage";
import NewDegradationProfilePage from "@/pages/NewDegradationProfilePage";
import DegradationProfileDetailPage from "@/pages/DegradationProfileDetailPage";

// Post-v2 — new public docs (capability-level, no internals).
import DocsFeaturesPage from "@/pages/marketing/docs/DocsFeaturesPage";
import DocsHowItWorksPage from "@/pages/marketing/docs/DocsHowItWorksPage";
import DocsEvidencePage from "@/pages/marketing/docs/DocsEvidencePage";
import DocsUseCasesPage from "@/pages/marketing/docs/DocsUseCasesPage";

function LogoutRoute() {
    const { logout } = useAuth();
    useEffect(() => {
        logout();
    }, [logout]);
    return <Navigate to="/login" replace />;
}

// Phase 9 — generic "carry over search + query" permanent redirect. The
// client-side equivalent of a 301; preserves any existing querystring so
// external links with ?tab=… etc. survive the IA restructure.
function RedirectWithSearch({ to }) {
    const loc = useLocation();
    return <Navigate to={`${to}${loc.search || ""}${loc.hash || ""}`} replace />;
}

// /runs/:id needs its path param substituted at redirect time.
function RedirectRunDetail() {
    const loc = useLocation();
    const id = loc.pathname.replace(/^\/runs\//, "");
    return <Navigate to={`/analyse/runs/${id}${loc.search || ""}`} replace />;
}

function ForbiddenPage() {
    return (
        <div
            className="min-h-[60vh] flex items-center justify-center text-ux-text-3"
            data-testid="forbidden-page"
        >
            <div className="text-center max-w-sm">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ux-text-3 mb-2">
                    403
                </div>
                <h1 className="text-2xl font-semibold text-ux-text-1">Forbidden</h1>
                <p className="text-sm mt-2">
                    This area requires a superadmin account. Ask another superadmin to elevate
                    your role, or sign in with a superadmin account.
                </p>
            </div>
        </div>
    );
}

export default function App() {
    return (
        <ErrorBoundary>
            <HelmetProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <div
                            data-build={BUILD_VERSION}
                            data-build-version={BUILD_VERSION}
                            style={{ display: "contents" }}
                        >
                        <RouteProgress />
                    <Toaster
                        position="top-right"
                        theme="dark"
                        richColors
                        toastOptions={{
                            style: {
                                background: "var(--ux-bg-1)",
                                border: "1px solid var(--ux-border-1)",
                                color: "var(--ux-text-1)",
                            },
                        }}
                    />
                    <Routes>
                        {/* Public marketing site (Phase 16 Gate 4) */}
                        <Route element={<PublicLayout />}>
                            <Route index element={<HomePage />} />
                            <Route path="/features" element={<FeaturesPage />} />
                            <Route path="/demo" element={<PublicDemoPage />} />
                            <Route path="/security" element={<SecurityPage />} />
                            <Route path="/docs" element={<DocsPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/privacy" element={<PrivacyPage />} />
                            <Route path="/aup" element={<AupPage />} />
                            {/* Phase 10 — new public marketing pages */}
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/use-cases" element={<UseCasesPage />} />
                            <Route path="/evidence" element={<EvidencePage />} />
                            <Route path="/faq" element={<FaqPage />} />
                        </Route>

                        {/* Auth + commercial flows */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/logout" element={<LogoutRoute />} />
                        <Route path="/invite" element={<AcceptInvitePage />} />
                        <Route
                            path="/subscription-required"
                            element={<SubscriptionRequiredPage />}
                        />

                        {/* Phase 19 — CP4 preview / screenshot route.
                            Public (no auth) standalone route that mounts
                            the battlespace map, dual-mode placement, and
                            three results panels with synthetic data so
                            the CP4 screenshot has something to capture
                            without touching the production scenarios
                            surface. */}
                        <Route
                            path="/battlespace"
                            element={
                                <Suspense fallback={<div style={{ padding: 16 }}>Loading battlespace editor…</div>}>
                                    <BattlespaceEditorPage />
                                </Suspense>
                            }
                        />
                        {/* Legacy redirect — earlier links used /phase19/preview. */}
                        <Route
                            path="/phase19/preview"
                            element={<Navigate to="/battlespace" replace />}
                        />

                        {/* Public docs — capability-level story. The old
                            /api/static/docs/... user guide has been retired;
                            any legacy URL redirects to /docs. */}
                        <Route element={<PublicLayout />}>
                            <Route path="/docs/features" element={<DocsFeaturesPage />} />
                            <Route path="/docs/how-it-works" element={<DocsHowItWorksPage />} />
                            <Route path="/docs/evidence" element={<DocsEvidencePage />} />
                            <Route path="/docs/use-cases" element={<DocsUseCasesPage />} />
                            {/* Phase 8c — public demos gallery. Pre-baked
                                kinematic intercept trajectories rendered in
                                the existing Trajectory3D viewer. No auth,
                                no live runs, no disclaimer (per human
                                direction in Phase 8c brief). */}
                            <Route path="/demos" element={<PublicDemosListPage />} />
                            <Route path="/demos/:slug" element={<PublicDemoViewerPage />} />
                        </Route>
                        <Route
                            path="/docs/system-overview"
                            element={<Navigate to="/docs" replace />}
                        />
                        <Route path="/guide" element={<Navigate to="/docs" replace />} />
                        <Route path="/user-guide" element={<Navigate to="/docs" replace />} />

                        {/* ─────────── Phase 9 back-compat redirects ─────────── */}
                        <Route
                            path="/presets"
                            element={<RedirectWithSearch to="/plan/scenarios" />}
                        />
                        <Route
                            path="/presets/wizard"
                            element={<RedirectWithSearch to="/plan/scenarios" />}
                        />
                        {/* Pass 4 — PresetDetailPage retired. Any legacy
                            /presets/:family/:name bookmark now lands on
                            the Scenarios library. The raw YAML surface
                            for that preset lives in /admin/raw-presets. */}
                        <Route
                            path="/presets/:family/:name"
                            element={<RedirectWithSearch to="/plan/scenarios" />}
                        />
                        <Route path="/runs" element={<RedirectWithSearch to="/run/history" />} />
                        <Route path="/runs/new" element={<RedirectWithSearch to="/plan/scenarios" />} />
                        <Route path="/runs/:id" element={<RedirectRunDetail />} />
                        <Route
                            path="/reviewer"
                            element={<RedirectWithSearch to="/review/inbox" />}
                        />

                        {/* ─────────── Authenticated console (Phase 9 IA) ─────────── */}
                        <Route
                            element={
                                <RequireAuth>
                                    <AppShell />
                                </RequireAuth>
                            }
                        >
                            {/* /console lands the operator on Run History. */}
                            <Route
                                path="/console"
                                element={<Navigate to="/run/history" replace />}
                            />

                            {/* PLAN */}
                            <Route path="/plan/scenarios" element={
                                <LibraryErrorBoundary pageLabel="Scenarios">
                                    <ScenariosLibraryPage />
                                </LibraryErrorBoundary>
                            } />
                            <Route
                                path="/plan/scenarios/wizard"
                                element={<RedirectWithSearch to="/plan/scenarios" />}
                            />
                            {/* Pass 4 — PresetDetailPage retired. Legacy
                                bookmark paths redirect to Scenarios. */}
                            <Route
                                path="/plan/scenarios/:family/:name"
                                element={<RedirectWithSearch to="/plan/scenarios" />}
                            />

                            {/* PLAN — Workbench v2 */}
                            <Route
                                path="/workbench/:scenarioId"
                                element={<WorkbenchPage />}
                            />

                            {/* PLAN — Airframes library */}
                            <Route
                                path="/plan/airframes"
                                element={
                                    <LibraryErrorBoundary pageLabel="Airframes">
                                        <AirframesPage />
                                    </LibraryErrorBoundary>
                                }
                            />

                            {/* Pass G — PLAN — Physics-airframe (FDM
                                registry) read-only catalogue */}
                            <Route
                                path="/plan/physics-airframes"
                                element={
                                    <LibraryErrorBoundary pageLabel="Physics airframes">
                                        <PhysicsAirframesCataloguePage />
                                    </LibraryErrorBoundary>
                                }
                            />

                            {/* PLAN — Environments library (Module 3) */}
                            <Route
                                path="/plan/environments"
                                element={
                                    <LibraryErrorBoundary pageLabel="Environments">
                                        <EnvironmentsPage />
                                    </LibraryErrorBoundary>
                                }
                            />

                            {/* PLAN — Terrain + Obstacles libraries (Module 4) */}
                            <Route
                                path="/plan/terrain"
                                element={<TerrainPage />}
                            />
                            <Route
                                path="/plan/obstacles"
                                element={<ObstaclesPage />}
                            />

                            {/* PLAN — Threats library (Module 5) */}
                            <Route
                                path="/plan/threats"
                                element={
                                    <LibraryErrorBoundary pageLabel="Threats">
                                        <ThreatsPage />
                                    </LibraryErrorBoundary>
                                }
                            />

                            {/* PLAN — Guidance profiles library (Module 6) */}
                            <Route
                                path="/plan/guidance-profiles"
                                element={
                                    <LibraryErrorBoundary pageLabel="Guidance profiles">
                                        <GuidanceProfilesPage />
                                    </LibraryErrorBoundary>
                                }
                            />

                            {/* PLAN — Engagements library (Module 7) */}
                            <Route
                                path="/plan/engagements"
                                element={<EngagementsPage />}
                            />

                            {/* RUN */}
                            {/* Pass 3 — Quick Sim: the non-expert entry.
                                Three curated pickers + one Run button. */}
                            <Route path="/quick-sim" element={<QuickSimPage />} />
                            {/* Pass 5 — Matrix run: analyst N×M(×K) batch. */}
                            <Route path="/run/matrix" element={<MatrixRunPage />} />
                            <Route
                                path="/run/matrix/:matrixJobId"
                                element={<MatrixRunResultsPage />}
                            />
                            {/* Phase 3 (ROE) — Reverse Optimisation Engine */}
                            <Route
                                path="/run/optimisation"
                                element={<OptimisationListPage />}
                            />
                            <Route
                                path="/run/optimisation/new"
                                element={<OptimisationNewPage />}
                            />
                            <Route
                                path="/run/optimisation/:studyId"
                                element={<OptimisationStudyDetailPage />}
                            />
                            {/* Phase 10 — Pk Effectiveness Engine v1 */}
                            {/* Phase 14.6 retired the bare /pk-campaigns
                                list route; the canonical :campaignId
                                detail route is preserved (unified
                                /campaigns/:id dispatcher routes here). */}
                            <Route
                                path="/pk-campaigns/:campaignId"
                                element={<PkCampaignDetailPage />}
                            />
                            {/* Phase 13 — Geometry Randomisation Engine */}
                            {/* Phase 14.6 retired the bare /geometry-campaigns
                                list route (same disposition as Pk above). */}
                            <Route
                                path="/geometry-campaigns/:campaignId"
                                element={<GeometryCampaignDetailPage />}
                            />
                            {/* Phase 15.1 — Optimisation campaign canonical
                                deep-link. Bare /optimisation-campaigns list
                                route is intentionally NOT mounted; the
                                unified /campaigns surface is the entry point. */}
                            <Route
                                path="/optimisation-campaigns/:campaignId"
                                element={<OptimisationCampaignDetailPage />}
                            />
                            {/* Phase 14.5 — Unified Campaign Builder UX.
                                Single `/campaigns` route consolidates pk +
                                geometry + degradation campaign creation.
                                Legacy `/pk-campaigns` + `/geometry-campaigns`
                                stay mounted above as deep-link entry points. */}
                            <Route path="/campaigns" element={<CampaignsListPage />} />
                            {/* Phase 17 — unified campaign builder + detail dispatch.
                                /campaigns/new → unified builder.
                                /campaigns/:id → tries unified first then falls
                                back to legacy pk/geometry/optimisation. */}
                            <Route path="/campaigns/new" element={<UnifiedCampaignBuilderPage />} />
                            <Route
                                path="/campaigns/:campaignId"
                                element={<CampaignDetailDispatchPage />}
                            />
                            {/* Phase 17 — legacy builder route preserved
                                under a separate URL for emergency use; not
                                linked from any nav surface. */}
                            <Route path="/campaigns/legacy/new" element={<NewCampaignWizardPage />} />
                            <Route
                                path="/degradation-profiles"
                                element={<DegradationProfilesListPage />}
                            />
                            <Route
                                path="/degradation-profiles/new"
                                element={<NewDegradationProfilePage />}
                            />
                            <Route
                                path="/degradation-profiles/:profileId"
                                element={<DegradationProfileDetailPage />}
                            />
                            <Route
                                path="/run/new"
                                element={<RedirectWithSearch to="/plan/scenarios" />}
                            />
                            <Route path="/run/history" element={<RunsListPage />} />
                            <Route path="/run/jobs" element={<JobsPage />} />

                            {/* ANALYSE */}
                            <Route path="/analyse/runs/:id" element={<RunDetailPage />} />
                            <Route path="/analyse/compare" element={<CompareRunsPage />} />
                            <Route path="/analyse/golden-runs" element={<GoldenRunsPage />} />

                            {/* MODELS & DATA */}
                            <Route path="/models/registry" element={<ModelsRegistryPage />} />
                            <Route
                                path="/models/calibration"
                                element={<CalibrationSessionsPage />}
                            />
                            <Route path="/models/logs" element={<LogsPage />} />
                            {/* Pass 4 — /models/terrain-and-weather
                                triplicated /plan/terrain + /plan/obstacles
                                + /plan/environments. Retired; 301 to
                                the closest user intent. */}
                            <Route
                                path="/models/terrain-and-weather"
                                element={
                                    <RedirectWithSearch to="/plan/terrain" />
                                }
                            />

                            {/* REVIEW */}
                            <Route path="/review/inbox" element={<ReviewerInboxPage />} />

                            {/* Phase 8c — public demos gallery. Both
                                /demos and /demos/:slug are mounted in the
                                public-layout block above (lines ~198) so
                                they render un-authed. */}
                            <Route path="/parameter-sets" element={
                                <LibraryErrorBoundary pageLabel="Parameter sets">
                                    <ParameterSetsPage />
                                </LibraryErrorBoundary>
                            } />
                            <Route
                                path="/parameter-sets/:id"
                                element={<ParameterSetDetailPage />}
                            />
                            {/* Pass 4 — BackendPage retired. The runtime
                                self-check lives at /admin/engineering now. */}
                            <Route
                                path="/backend"
                                element={<RedirectWithSearch to="/admin/engineering" />}
                            />
                            <Route path="/help" element={<HelpPage />} />
                            {/* Phase 14.12 — Campaigns User Guide. Single-source
                                operator-facing reference for the unified Campaign
                                Builder. Reached via the "Help & user guide" link
                                in WizardShell, or directly by URL. */}
                            <Route
                                path="/help/campaigns"
                                element={<CampaignsUserGuidePage />}
                            />
                            {/* Phase 14.15 — superadmin brochure-lead queue.
                                Backend enforces role; the page degrades to
                                an inline error if the API rejects. */}
                            <Route
                                path="/admin/brochure-leads"
                                element={<AdminBrochureLeadsPage />}
                            />
                            <Route
                                path="/account/change-password"
                                element={<ChangePasswordPage />}
                            />

                            {/* ADMIN — each route gates via RequireAuth requireAdmin */}
                            <Route
                                path="/admin"
                                element={
                                    <RequireAuth requireAdmin>
                                        <AdminHomePage />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/admin/users"
                                element={
                                    <RequireAuth requireAdmin>
                                        <UsersAdminPage />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/admin/tenants"
                                element={
                                    <RequireAuth requireAdmin>
                                        <AdminTenantsPage />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/admin/engineering"
                                element={
                                    <RequireAuth requireAdmin>
                                        <AdminEngineeringPage />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/admin/training-data"
                                element={
                                    <RequireAuth requireAdmin>
                                        <AdminTrainingDataPage />
                                    </RequireAuth>
                                }
                            />
                            <Route
                                path="/admin/raw-presets"
                                element={
                                    <RequireAuth requireAdmin>
                                        <AdminRawPresetsPage />
                                    </RequireAuth>
                                }
                            />

                            <Route path="/403" element={<ForbiddenPage />} />
                        </Route>

                        {/* Catch-all → marketing 404 */}
                        <Route path="*" element={<PublicLayout />}>
                            <Route path="*" element={<NotFoundPage />} />
                        </Route>
                    </Routes>
                        <span
                            data-testid="app-build-marker"
                            style={{
                                position: "fixed",
                                right: "8px",
                                bottom: "4px",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontVariantNumeric: "tabular-nums",
                                fontSize: "11px",
                                color: "#5A6B7C",
                                pointerEvents: "none",
                                userSelect: "none",
                                zIndex: 1,
                                opacity: 0.7,
                                letterSpacing: "0.02em",
                            }}
                        >
                            build {BUILD_VERSION}
                        </span>
                        </div>
                </BrowserRouter>
            </AuthProvider>
            </HelmetProvider>
        </ErrorBoundary>
    );
}
